<template>
  <div class="dealers-item">
    <form-container :loading="loading" :hasData="!!model" :deleted="!!deletedAt" @goBack="redirect">
      <template #goback>Назад к списку дилеров</template>
      <template #heading>
        <h1 v-if="model.title">{{ model.title }}</h1>
        <h1 v-else-if="$props.createMode">Создать нового дилера</h1>
      </template>
      <template #dates>
        <span v-if="createdAt">Создан: {{ createdAt }}</span>
        <span v-if="updatedAt">Обновлен: {{ updatedAt }}</span>
        <span v-if="deletedAt">Удален: {{ deletedAt }}</span>
      </template>
      <template #content>
        <dealers-form
          ref="form"
          :model="model"
          @imageUpdated="imageUpdated"
          @addPhone="addPhone"
          @addAddress="addAddress"
          @addTestDriveEmail="addTestDriveEmail"
          @addServiceContractEmail="addServiceContractEmail"
          @addMaintenanceEmail="addMaintenanceEmail"
          @addCarcaseRepairEmail="addCarcaseRepairEmail"
        />
      </template>
      <template #actions v-if="$props.createMode">
        <button v-if="editable" class="info" @click="getLocation">Определить координаты из адреса</button>
        <button @click="onCreate" class="info">Создать</button>
      </template>
      <template #actions v-else>
        <button v-if="editable" class="info" @click="getLocation">Определить координаты из адреса</button>
        <button v-if="editable" class="red" @click="onRemove">Удалить</button>
        <button v-if="editable" class="info" @click="onSave">Сохранить</button>
      </template>
    </form-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import Moment from 'moment';

import options from '@/config/dropzone-options';

import DealerModel from '@/models/dealer';

import DealersForm from './DealersForm.vue';
import FormContainer from '@/components/common/FormContainer.vue';

export default {
  name: 'dealers-item',

  props: {
    createMode: Boolean,
  },

  components: {
    FormContainer,
    DealersForm,
  },

  watch: {
    currentItem(model) {
      this.model = new DealerModel(model);
    },
  },

  computed: {
    ...mapGetters({
      loading: 'dealers/loading',
      currentItem: 'dealers/currentItem',
      location: 'util/location',
      statuses: 'dealers/statuses',
      cities: 'dealers/cities',
    }),

    createdAt() {
      return this.model ? this.formatDate(this.model.created_at) : null;
    },

    updatedAt() {
      return this.model ? this.formatDate(this.model.updated_at) : null;
    },

    deletedAt() {
      return this.model ? this.formatDate(this.model.deleted_at) : null;
    },

    editable() {
      return !this.model.deleted_at;
    },
  },

  methods: {
    ...mapActions({
      clearCurrentItem: 'dealers/clearCurrentItem',
      fetchOne: 'dealers/fetchOne',
      create: 'dealers/create',
      remove: 'dealers/remove',
      save: 'dealers/save',
      getCoords: 'util/getCoords',
      getConfig: 'dealers/getConfig',
    }),

    imageUpdated(url) {
      this.model.image_url = url;
    },

    redirect() {
      this.$router.push({ name: 'dealers' });
    },

    formatDate(date) {
      return date ? Moment(date).format('llll') : null;
    },

    addPhone(phone) {
      this.model.phones.push(phone);
    },

    addAddress(address) {
      this.model.address.push(address);
    },

    addTestDriveEmail(email) {
      this.model.email_test_drive.push(email);
    },

    addServiceContractEmail(email) {
      this.model.email_service_contract.push(email);
    },

    addMaintenanceEmail(email) {
      this.model.email_maintenance.push(email);
    },

    addCarcaseRepairEmail(email) {
      this.model.email_carcase_repair.push(email);
    },

    toast(msg, type) {
      if (type === 'success') {
        this.$snotify.success(msg, null, {
          showProgressBar: false,
        });
      }
      if (type === 'error') {
        this.$snotify.error(msg, null, {
          showProgressBar: false,
        });
      }
    },

    async getLocation() {
      const address = this.model.address;

      await this.getCoords(address);

      if (this.location) {
        this.model.latitude = `${this.location.lat}`;
        this.model.longitude = `${this.location.lon}`;

        this.$snotify.success('Координаты успешно получены.', null, {
          showProgressBar: false,
        });
      } else {
        this.$snotify.info('Координаты не найдены. Уточните адрес.', null, {
          showProgressBar: false,
        });
      }
    },

    async validateForm() {
      return await this.$refs.form.validate();
    },

    async onSave() {
      const validated = await this.validateForm();

      if (validated) {
        const err = await this.save(this.model.dto);
        if (!err) {
          this.toast('Изменения успешно сохранены.', 'success');
        } else {
          this.toast('Произошла ошибка.', 'error');
        }
      }
    },

    async onRemove() {
      const err = await this.remove(this.model);

      if (!err) {
        this.toast('Дилер успешно удалён.', 'success');
        return this.$router.push({ name: 'dealers' });
      } else {
        this.toast('Произошла ошибка.', 'error');
      }
    },

    async onCreate() {
      const validated = await this.validateForm();

      if (validated) {
        const err = await this.create(this.model.dto);
        if (!err) {
          this.toast('Дилер успешно создан.', 'success');

          return this.$router.push({
            name: 'dealers-item',
            params: { id: this.currentItem.id },
          });
        } else {
          this.toast('Произошла ошибка.', 'error');
        }
      }
    },
  },

  data() {
    return {
      model: null,
    };
  },

  beforeDestroy() {
    this.clearCurrentItem();
  },

  async beforeMount() {
    if (!this.cities.length || !this.statuses.length) {
      await this.getConfig();
    }
    if (!this.$props.createMode) {
      await this.fetchOne(this.$route.params.id);
    } else {
      this.model = new DealerModel();
    }
  },
};
</script>

<style lang="scss" scoped>
.dealers-item {
  flex: 1;
}
</style>
